import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "../lib/auth";
import { apiErr } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (e2) {
      setErr(apiErr(e2, "Sign in failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-[#FBF1EC] font-body lg:grid-cols-2" data-testid="admin-login">
      {/* Left brand panel */}
      <div className="relative hidden flex-col justify-between p-10 lg:flex">
        <div className="flex items-center gap-3">
          <img src="/pooja-art/lakshmi-pooja.jpg" alt="" className="h-10 w-10 rounded-lg object-cover ring-1 ring-[#EBB59F]" />
          <span className="font-heading text-lg font-semibold text-[#2C241B]">PurohithConnect™</span>
        </div>
        <div>
          <p className="font-body text-[12px] font-semibold uppercase tracking-[0.16em] text-[#C1440E]">Platform operations</p>
          <h1 className="mt-3 font-heading text-5xl font-semibold leading-[1.05] text-[#7A2E1E]">One place to run every sacred service.</h1>
          <p className="mt-5 max-w-md font-body text-[15px] text-[#6B5044]">Manage the marketplace, uphold priest quality, publish trusted ceremony guidance, and understand how families use the platform.</p>
        </div>
        <p className="flex items-center gap-2 font-body text-xs text-[#A79C8A]">
          <Lock size={13} /> Protected by JWT auth and role-based access control
        </p>
      </div>

      {/* Right sign-in card */}
      <div className="flex items-center justify-center p-6 lg:bg-white">
        <div className="w-full max-w-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FBEDE7]">
            <Lock size={18} className="text-[#D84315]" />
          </span>
          <p className="mt-6 font-body text-[12px] font-semibold uppercase tracking-[0.16em] text-[#C1440E]">Restricted access</p>
          <h2 className="mt-1 font-heading text-3xl font-semibold text-[#2C241B]">Sign in to the console</h2>
          <p className="mt-2 font-body text-sm text-[#8A8072]">Use an account whose role is <code className="rounded bg-[#F5F1E8] px-1">admin</code> or <code className="rounded bg-[#F5F1E8] px-1">super_admin</code>.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="font-body text-sm font-medium text-[#2C241B]">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@purohithconnect.in" data-testid="login-email"
                className="mt-1.5 h-11 w-full rounded-lg border border-[#E5DFD3] bg-white px-3.5 font-body text-sm outline-none focus:border-[#E0A98F]"
              />
            </div>
            <div>
              <label className="font-body text-sm font-medium text-[#2C241B]">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Your secure password" data-testid="login-password"
                className="mt-1.5 h-11 w-full rounded-lg border border-[#E5DFD3] bg-white px-3.5 font-body text-sm outline-none focus:border-[#E0A98F]"
              />
            </div>
            {err && <p className="rounded-lg bg-[#FDECEA] px-3 py-2 font-body text-sm text-[#C62828]" data-testid="login-error">{err}</p>}
            <button
              type="submit" disabled={busy} data-testid="login-submit"
              className="h-11 w-full rounded-lg bg-[#D84315] font-body text-sm font-semibold text-white transition-colors hover:bg-[#BF360C] disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in securely"}
            </button>
          </form>
          <p className="mt-4 text-center font-body text-xs text-[#A79C8A]">Access is logged. Never share production credentials.</p>
        </div>
      </div>
    </div>
  );
}
